import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Certification = () => {
  const [certificationUrl, setCertificationUrl] = useState<string | null>(null);
  const [practitionerName, setPractitionerName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState<number>(800);

  useEffect(() => {
    loadCertification();
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setPageWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [certificationUrl]);

  const loadCertification = async () => {
    try {
      const { data, error } = await supabase.rpc('get_practitioner_public_display');

      if (error) throw error;

      if (data && data.length > 0) {
        const practitioner = data[0];
        setCertificationUrl(practitioner.certification_url);
        setPractitionerName(practitioner.practitioner_name || "");
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la certification:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-serif text-primary mb-4 text-center">
            Ma Certification
          </h1>
          
          {practitionerName && (
            <p className="text-center text-muted-foreground mb-8">
              Diplôme de {practitionerName}
            </p>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : certificationUrl ? (
            <div className="bg-card rounded-2xl shadow-lg p-4 md:p-8">
              {certificationUrl.toLowerCase().endsWith('.pdf') ? (
                <div ref={containerRef} className="w-full rounded-lg overflow-hidden shadow-md bg-white">
                  <Document
                    file={certificationUrl}
                    loading={
                      <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      width={pageWidth}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Document>
                </div>
              ) : (
                <img
                  src={certificationUrl}
                  alt="Certification / Diplôme"
                  className="w-full h-auto rounded-lg shadow-md"
                />
              )}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                Aucune certification n'a encore été ajoutée.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Certification;
